import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';

import { MijnNavigationEventsList, MijnRouteKey, MijnRoutes } from '@ap/mijn-navigatie';
import { MijnOmgevingTemplates } from '@ap/mijn-template';
import { ApWebcomponentComponent, WebcomponentEvent } from '@ap/webcomponent';

import dynamicTitles from './dynamic-titles.json';
import { BusinessCustomerGuard } from './guards/business-customer/business-customer.guard';
import { CorrectRouteGuard } from './guards/correct-route/correct-route.guard';
import { DynamicProcessGuard } from './guards/dynamic-process/dynamic-process.guard';
import { PegaHuismapGuard } from './guards/pega-huismap/pega-huismap.guard';
import { PendingProductionGuard } from './guards/pending-production/pending-production.guard';
import { MijnContextErrorComponent } from './mijn-error/mijn-context-error/mijn-context-error.component';
import { ErrorContext } from './mijn-error/mijn-context-error/mijn.context-error.model';
import { PhoneNumberValidationComponent } from './phonenumber-validation/phonenumber-validation.component';

export const defaultEvents: WebcomponentEvent[] = MijnNavigationEventsList.map((eventName) => ({ name: eventName, action: eventName }));

const urlNotificatieBeheer = '/online/webcomponents/mijn-klant-notificatie-beheer';
const identifierNotificatieBeheer = 'mijn-klant-notificatie-beheer-webcomponent';
const urlBeleggenPortfolio = '/online/webcomponents/mijn-beleggen-portfolio';

const routes: Routes = [
    {
        path: MijnRoutes.get(MijnRouteKey.AFLOSVRIJ_CHECK).path,
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/aflosvrij-check',
            identifier: 'mijn-aflosvrij-check-webcomponent',
            allowBusiness: false,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ADDRESS_BOOK).path,
        component: ApWebcomponentComponent,
        title: 'Adresboek',
        data: {
            deployUrl: '/online/webcomponents/mijn-klant-adresboek',
            identifier: 'mijn-klant-adresboek-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ASN_MIGRATION_ONBOARDING).path,
        component: ApWebcomponentComponent,
        canActivate: [PendingProductionGuard],
        data: {
            deployUrl: '/online/webcomponents/mijn-klant-onboarding',
            identifier: 'mijn-klant-onboarding-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        },
        children: [
            {
                path: 'mobile',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-onboarding',
                    identifier: 'mijn-klant-onboarding-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.MOBILE,
                    isMobile: true,
                    requiresAuth: false
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.CHANGE_DIGITAL_ADDRESS).path,
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-digitaal-adres-wijzigen',
            identifier: 'mijn-digitaal-adres-wijzigen-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.EXTERNE_BETALING).path,
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-externe-betaling',
            identifier: 'mijn-externe-betaling-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ZAKELIJK_DIGIPAS_VERVANGEN).path,
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-zakelijk-digipas-vervangen',
            identifier: 'mijn-zakelijk-digipas-vervangen-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.OVERVIEW).path,
        title: 'Overzicht',
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: ApWebcomponentComponent,
                canActivate: [BusinessCustomerGuard],
                data: {
                    deployUrl: '/online/webcomponents/mijn-overzicht',
                    identifier: 'mijn-overzicht',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.OVERVIEW_EDIT).path,
                title: 'Overzicht Aanpassen',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overzicht',
                    identifier: 'mijn-overzicht',
                    path: '/overzicht-aanpassen',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.OVERVIEW_ACCOUNTS).path,
                title: 'Rekeningen Overzicht',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overzicht',
                    identifier: 'mijn-overzicht',
                    path: '/rekeningen-overzicht',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.MAKE_TRANSFER).path,
        title: 'Overboeken',
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overboeken',
                    identifier: 'mijn-overboeken-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENTS_OVERVIEW).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overboeken',
                    identifier: 'mijn-overboeken-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.PAYMENTS_OVERVIEW).internalPath,
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENTS_SIGN).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overboeken',
                    identifier: 'mijn-overboeken-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.PAYMENTS_SIGN).internalPath,
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENTS_SUMMARY).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overboeken',
                    identifier: 'mijn-overboeken-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.PAYMENTS_SUMMARY).internalPath,
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENTS_PIGGYBANK_SUMMARY).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-overboeken',
                    identifier: 'mijn-overboeken-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.PAYMENTS_PIGGYBANK_SUMMARY).internalPath,
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PENDING_PAYMENTS).path,
        title: 'Verzendlijst',
        canActivate: [BusinessCustomerGuard, PendingProductionGuard],
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-pending-payments',
            identifier: 'mijn-pending-payments-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES).path,
        title: 'Betaalbatches',
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_AFGEROND_BESTAND).path,
                title: 'Batch details',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_AFGEROND_BESTAND).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_BATCH_DETAILS).path,
                title: 'Batch details',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_BATCH_DETAILS).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_BATCH_BESTAND_DETAILS).path,
                title: 'Batchbestand details',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_BATCH_BESTAND_DETAILS).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_RESULT).path,
                title: 'Batchbetaling gelukt',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_RESULT).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_UPLOAD).path,
                title: 'Uploaden',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_UPLOAD).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_SIGN).path,
                title: 'Ondertekenen',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-betalen-betaalbatches',
                    identifier: 'mijn-betalen-betaalbatches-webcomponent',
                    allowBusiness: true,
                    path: MijnRoutes.get(MijnRouteKey.PAYMENT_BATCHES_SIGN).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.HUISMAP).path,
        canActivate: [PegaHuismapGuard],
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-huismap',
            identifier: 'mijn-huismap-webcomponent',
            allowBusiness: false,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES).path,
        title: 'Incassobatches',
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_DETAILS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_DETAILS).internalPath,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_ERROR).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_ERROR).internalPath,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_SIGN).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_SIGN).internalPath,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_SUCCESS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_SUCCESS).internalPath,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_UPLOAD).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_UPLOAD).internalPath,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_UITVALRAPPORTAGES).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-incassobatches',
                    identifier: 'mijn-incassobatches-webcomponent',
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.INCASSO_BATCHES_UITVALRAPPORTAGES).internalPath,
                    events: defaultEvents
                }
            }
        ],
        data: {
            breadcrumb: 'Incassobatches'
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.AUTO_TOP_UP).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Betaalrekening aanvullen',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-auto-top-up',
            identifier: 'mijn-auto-top-up-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.INVESTMENT_OVERVIEW).path,
        title: 'Portfolio',
        canActivate: [CorrectRouteGuard],
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                canActivate: [CorrectRouteGuard],
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_VIEW).path,
                component: ApWebcomponentComponent,
                canActivate: [CorrectRouteGuard],
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_ORDER).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/order',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_ORDER_OVERVIEW).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/order-overzicht',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_ORDER_HISTORY).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/order-history',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_MANAGE).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/beheer',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_FINANCIAL_RETURN).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/financieel-rendement',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_CASH).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/gelddeel',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_PORTFOLIO).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/portefeuille',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_ORDERS).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/orders',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_SUSTAINABLE_RETURN).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/duurzaam-rendement',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_PLANNER_OVERVIEW).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/planner-overzicht',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_PLANNER).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/planner',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_CANCEL_ACCOUNT).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/rekening-opheffen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_COMPLETE).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/resultaat',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_ORDER_ALLOCATION).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/automatisch-aankopen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INVESTMENT_SIGN).path,
                canActivate: [CorrectRouteGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: urlBeleggenPortfolio,
                    identifier: 'mijn-beleggen-portfolio-webcomponent',
                    path: ':id/signeren',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.LOAN_APPLY).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Lening aanvragen',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-leningen-aanvragen',
            identifier: 'mijn-leningen-aanvragen-webcomponent',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.LOAN_APPLY_MANAGE).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Lening aanvragen',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-leningen-aanvragen',
            identifier: 'mijn-leningen-aanvragen-webcomponent',
            path: 'beheer',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PERSONAL_LOAN_OVERVIEW).path,
        title: 'Persoonlijke lening',
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-persoonlijke-lening',
                    identifier: 'mijn-persoonlijke-lening-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.PERSONAL_LOAN_VIEW).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-persoonlijke-lening',
                    identifier: 'mijn-persoonlijke-lening-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PENSION_EXPANSION_REQUEST_INVESTMENT).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Pensioen beleggen aanvragen',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/my-pension-expansion-request',
            identifier: 'my-pension-expansion-request-webcomponent',
            path: 'beleggen',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PENSION_EXPANSION_REQUEST_DEPOSIT).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Pensioen deposito aanvragen',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/my-pension-expansion-request',
            identifier: 'my-pension-expansion-request-webcomponent',
            path: 'deposito',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.MESSAGE_OVERVIEW).path,
        component: ApWebcomponentComponent,
        title: 'Berichten',
        data: {
            deployUrl: '/online/webcomponents/mijn-berichten-overzicht',
            identifier: 'mijn-berichten-overzicht-webcomponent',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.MESSAGE_VIEW).path,
        title: 'Berichten',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-berichten-overzicht',
            identifier: 'mijn-berichten-overzicht-webcomponent',
            events: defaultEvents
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PAYMENT_REQUEST).path,
        component: ApWebcomponentComponent,
        title: 'Betaalverzoek',
        data: {
            deployUrl: '/online/webcomponents/mijn-betalen-betaalverzoek',
            identifier: 'mijn-betalen-betaalverzoek-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PAYMENT_REQUEST_FORM).path,
        component: ApWebcomponentComponent,
        title: 'Betaalverzoek',
        data: {
            deployUrl: '/online/webcomponents/mijn-betalen-betaalverzoek',
            identifier: 'mijn-betalen-betaalverzoek-webcomponent',
            path: 'betaalverzoek-aanmaken',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.DOCUMENT_OVERVIEW).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Documenten',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-documenten-overzicht',
            identifier: 'mijn-documenten-overzicht-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.MORTGAGE_OVERVIEW).path,
        title: 'Hypotheken',
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-home',
                    identifier: 'mijn-hypotheken-home-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_OVERVIEW_DETAILS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-home',
                    identifier: 'mijn-hypotheken-home-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.MORTGAGE_OVERVIEW_DETAILS).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_OVERVIEW_ENERGYLABEL).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-home',
                    identifier: 'mijn-hypotheken-home-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.MORTGAGE_OVERVIEW_ENERGYLABEL).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_NEXT_PAYMENT).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-home',
                    identifier: 'mijn-hypotheken-home-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.MORTGAGE_NEXT_PAYMENT).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_PREVIOUS_PAYMENTS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-home',
                    identifier: 'mijn-hypotheken-home-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.MORTGAGE_PREVIOUS_PAYMENTS).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_ADDITIONAL_PAYMENT).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek extra storten',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-extra-storten',
                    identifier: 'mijn-hypotheken-extra-storten-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INCREASE).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek verhogen',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-verhogen',
                    identifier: 'mijn-hypotheken-verhogen-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INCREASE_KNOWLEDGE_TEST).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Kennis- & ervaringstoets hypotheken',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-verhogen',
                    identifier: 'mijn-hypotheken-verhogen-webcomponent',
                    path: 'kennistoets',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INCREASE_REQUEST).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek verhogen',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-verhogen',
                    identifier: 'mijn-hypotheken-verhogen-webcomponent',
                    path: 'aanvraag',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INTEREST_ADJUST).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek renteverlenging',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-renteverlenging',
                    identifier: 'mijn-hypotheken-renteverlenging-webcomponent',
                    path: 'tussentijds-aanpassen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INTEREST_EXTEND).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek renteverlenging',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-renteverlenging',
                    identifier: 'mijn-hypotheken-renteverlenging-webcomponent',
                    path: 'regulier-verlengen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_BUILD_FUND_DECLARE).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek bouwdepot',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-bouwdepot',
                    identifier: 'mijn-hypotheken-bouwdepot-webcomponent',
                    path: 'declaratie',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_BUILD_FUND_TERMINATE).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek bouwdepot',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-bouwdepot',
                    identifier: 'mijn-hypotheken-bouwdepot-webcomponent',
                    path: 'beeindigen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_BUILD_FUND_NEW_ADDRESS).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek bouwdepot',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-bouwdepot',
                    identifier: 'mijn-hypotheken-bouwdepot-webcomponent',
                    path: 'adres-nieuwe-woning-doorgeven',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_CONVERT).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek aflossingsvrij omzetten',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-aflossingsvrij-omzetten',
                    identifier: MijnRoutes.get(MijnRouteKey.MORTGAGE_CONVERT).webcomponent,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_INTEREST_ONLY_CHECK).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek aflossingsvrij',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-aflossingsvrij',
                    identifier: 'mijn-hypotheken-aflossingsvrij-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_REDEEM).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheken aflossen',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-aflossen',
                    identifier: 'mijn-hypotheken-aflossen-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_CHANGE_COLLECTION_ACCOUNT).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek incassorekening',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-incassorekening',
                    identifier: 'mijn-hypotheken-incassorekening-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_HOUSE_VALUE).path,
                canActivate: [BusinessCustomerGuard],
                title: 'Hypotheek woningwaarde',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-woningwaarde/',
                    identifier: 'mijn-hypotheken-woningwaarde-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.MORTGAGE_DOCUMENTUPLOAD).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                title: 'Hypotheek documentupload',
                data: {
                    deployUrl: '/online/webcomponents/mijn-hypotheken-documentupload',
                    identifier: MijnRoutes.get(MijnRouteKey.MORTGAGE_DOCUMENTUPLOAD).webcomponent,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            }
        ],
        data: {
            breadcrumb: 'Mijn Hypotheek'
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PLANNED_PAYMENT).path,
        canActivate: [BusinessCustomerGuard],
        component: ApWebcomponentComponent,
        title: 'Transacties ingepland',
        data: {
            deployUrl: '/online/webcomponents/mijn-transacties-ingepland',
            identifier: 'mijn-transacties-ingepland-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PLANNED_PAYMENT_VIEW).path,
        canActivate: [BusinessCustomerGuard],
        component: ApWebcomponentComponent,
        title: 'Transacties ingepland',
        data: {
            deployUrl: '/online/webcomponents/mijn-transacties-ingepland',
            identifier: 'mijn-transacties-ingepland-webcomponent',
            allowBusiness: true,
            events: defaultEvents
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.COLLECTION).path,
        title: 'Transacties incasso',
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_ADD_PERMISSION).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_CHANGE_PERMISSION_LIST).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_REFUND).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_REFUSE).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_REMOVE_PERMISSION).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.COLLECTION_UPDATE_PERMISSION).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-incasso',
                    identifier: 'mijn-transacties-incasso-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ACCOUNT).path,
        title: 'Rekening',
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-rekening',
                    identifier: 'mijn-rekening-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.ACCOUNT_OVERVIEW).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-rekening',
                    identifier: 'mijn-rekening-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.BALANCE_CHART).path,
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-saldo-grafiek',
                    identifier: 'mijn-saldo-grafiek-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.BUDGETS).path,
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-budget',
                    identifier: 'mijn-budget-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.BUDGET_CREATE).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-budget',
                    identifier: 'mijn-budget-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE,
                    path: MijnRoutes.get(MijnRouteKey.BUDGET_CREATE).internalPath
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.BUDGET_DETAILS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-budget',
                    identifier: 'mijn-budget-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                    path: MijnRoutes.get(MijnRouteKey.BUDGET_DETAILS).internalPath
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.BUDGET_EDIT).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-budget',
                    identifier: 'mijn-budget-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE,
                    path: MijnRoutes.get(MijnRouteKey.BUDGET_EDIT).internalPath
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.FIXED_EXPENSES).path,
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-vaste-lasten',
                    identifier: 'mijn-vaste-lasten-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.INCOME_AND_EXPENSE).path,
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-geld-inzicht',
                    identifier: 'mijn-geld-inzicht-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INCOME_AND_EXPENSE_CATEGORY).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-geld-inzicht-categorie',
                    identifier: 'mijn-geld-inzicht-categorie-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.SECURITY_AWARENESS_TRAINING).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Security awareness',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-klant-security-awareness',
            identifier: 'mijn-klant-security-awareness',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PIGGY_BANKS).path,
        canActivate: [BusinessCustomerGuard],
        title: 'Spaarpotten',
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-spaarpot',
            identifier: 'mijn-spaarpot-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE).path,
        title: 'Zelfservice',
        children: [
            {
                path: '',
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_PRIVACY).path,
                children: [
                    {
                        path: '',
                        canActivate: [BusinessCustomerGuard],
                        title: 'Privacy',
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                            identifier: 'mijn-klant-zelfservice-webcomponent',
                            path: '/privacy',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.AI_CONSENT).path,
                        canActivate: [BusinessCustomerGuard],
                        title: 'Toestemming inzicht in je geld',
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ai-consent',
                            identifier: 'mijn-ai-consent-webcomponent',
                            allowBusiness: false,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.TELEPHONY_CONSENT).path,
                        canActivate: [BusinessCustomerGuard],
                        title: 'Toestemming Telefonie',
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-telephony-consent',
                            identifier: 'mijn-telephony-consent-webcomponent',
                            allowBusiness: false,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ACCOUNTS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/rekeningen',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_MANAGE_ACCESS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/persoon-toevoegen-verwijderen',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_SHARING_ACCESS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/toegang-gegevens-delen',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_MORTGAGES).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/hypotheken',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ONLINE_BANKING_CHILD).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/online-bankieren-kind',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_GELDZAKEN_KIND).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-geldzaken-kind',
                    identifier: 'mijn-klant-geldzaken-kind-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_HERIDENTIFICATIE).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-heridentificatie',
                    identifier: 'mijn-klant-heridentificatie-webcomponent',
                    allowBusiness: false,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_SAFETY_ACCESS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/veiligheid-en-toegang',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_TRANSACTIONS_DOWNLOAD).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-downloaden',
                    identifier: 'mijn-transacties-downloaden-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ACCOUNTBALANCES_DOWNLOAD).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-rekeningstanden-downloaden',
                    identifier: 'mijn-rekeningstanden-downloaden-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_AUTH_TOKENS).path,
                children: [
                    {
                        path: '',
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-tokens-beheer',
                            identifier: 'mijn-tokens-beheer-webcomponent',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_AUTH_TOKENS_REMOVE_DIGIPAS).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-tokens-beheer',
                            identifier: 'mijn-tokens-beheer-webcomponent',
                            path: '/digipas-verwijderen/:id',
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_MESSAGEPREFERENCES).path,
                children: [
                    {
                        path: '',
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-klant-toestemmingen',
                            identifier: 'mijn-klant-toestemmingen-webcomponent',
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_MESSAGE_PREFERENCES_NBA).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-klant-toestemmingen',
                            identifier: 'mijn-klant-toestemmingen-webcomponent',
                            path: '/nba',
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_TOEGANGSNAAM_WACHTWOORD_AANVRAGEN).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-toegangsnaam-wachtwoord-aanvragen',
                    identifier: 'mijn-toegangsnaam-wachtwoord-aanvragen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_TOKEN_LIMITS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-tokens-limieten',
                    identifier: 'mijn-tokens-limieten-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_TOKENS_AUTHENTICATION_HISTORY).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-tokens-authenticatie-historie',
                    identifier: 'mijn-tokens-authenticatie-historie-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_CONTACT_DETAILS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-contactgegevens',
                    identifier: 'mijn-klant-contactgegevens-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_CURRENT_AFFAIRS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-lopende-zaken',
                    identifier: 'mijn-klant-lopende-zaken-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_CHANGE_USERNAME_PASSWORD).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-tokens-digicode',
                    identifier: 'mijn-tokens-digicode-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_REFUSED_PAYMENTS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-transacties-geweigerd',
                    identifier: 'mijn-transacties-geweigerd-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL).path,
                title: 'iDeal-profiel',
                children: [
                    {
                        path: '',
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ideal-zelfservice',
                            identifier: 'mijn-ideal-zelfservice-webcomponent',
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                            events: defaultEvents
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_DETAILS).path,
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ideal-zelfservice',
                            identifier: 'mijn-ideal-zelfservice-webcomponent',
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                            path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_DETAILS).internalPath,
                            events: defaultEvents
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_ERROR).path,
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ideal-zelfservice',
                            identifier: 'mijn-ideal-zelfservice-webcomponent',
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT,
                            path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_ERROR).internalPath,
                            events: defaultEvents
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_MAKE_PROFILE).path,
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ideal-zelfservice',
                            identifier: 'mijn-ideal-zelfservice-webcomponent',
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE,
                            path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDEAL_MAKE_PROFILE).internalPath,
                            events: defaultEvents
                        }
                    }
                ],
                data: {
                    breadcrumb: 'Ideal'
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_IDIN).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-idin-zelfservice',
                    identifier: 'mijn-idin-zelfservice-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_PAYMENT_CARDS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-betaalpassen',
                    identifier: 'mijn-klant-betaalpassen-webcomponent',
                    contextErrorPath: MijnRoutes.get(MijnRouteKey.BUSINESS_CUSTOMER_BLOCKED).path,
                    allowSelfEmployed: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_PRODUCT_AANVRAGEN).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-product-aanvragen',
                    identifier: 'mijn-product-aanvragen',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ADD_PRODUCT).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-zelfservice',
                    identifier: 'mijn-klant-zelfservice-webcomponent',
                    path: '/product-toevoegen',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_UNBLOCK_DIGIPAS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-digipas-deblokkeren',
                    identifier: 'mijn-digipas-deblokkeren-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_PREFERREDACCOUNT).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-voorkeursrekening',
                    identifier: 'mijn-klant-voorkeursrekening-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_CANCELLED_ACCOUNTS).path,
                canActivate: [BusinessCustomerGuard],
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-rekeningen-opgeheven',
                    identifier: 'mijn-rekeningen-opgeheven-webcomponent',
                    allowBusiness: true,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ECOMMERCE).path,
                title: 'Opdracht goedkeuren',
                children: [
                    {
                        path: '',
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ecommerce-payments',
                            identifier: 'mijn-ecommerce-payments-webcomponent',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ECOMMERCE_SUMMARY).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ecommerce-payments',
                            identifier: 'mijn-ecommerce-payments-webcomponent',
                            path: '/overzicht',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ECOMMERCE_PAYMENT).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-ecommerce-payments',
                            identifier: 'mijn-ecommerce-payments-webcomponent',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_INTRODUCTION).path,
                title: 'Zelfservice',
                children: [
                    {
                        path: '',
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/seintjes',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_SECURITY).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/veiligheids-berichten',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_INCASSO).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/incasso-notificatie',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_SALDO).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/saldo-notificatie',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_SALDO_EDIT).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/saldo-notificatie/:id',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_PENDING_PAYMENT).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/ingepland-geweigerd-notificatie',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_PENDING_PAYMENT_EDIT).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/ingepland-geweigerd-notificatie/:id',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_TRANSACTION).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/transactie-notificatie',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    },
                    {
                        path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_ALERTS_TRANSACTION_EDIT).path,
                        canActivate: [BusinessCustomerGuard],
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: urlNotificatieBeheer,
                            identifier: identifierNotificatieBeheer,
                            path: '/transactie-notificatie/:id',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.ACTIE
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_THIRD_PARTY_ACCESS).path,
                children: [
                    {
                        path: '',
                        component: ApWebcomponentComponent,
                        data: {
                            deployUrl: '/online/webcomponents/mijn-klant-toegang-derden-beheer',
                            identifier: 'mijn-klant-toegang-derden-beheer',
                            allowBusiness: true,
                            events: defaultEvents,
                            dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                        }
                    }
                ]
            },
            {
                path: MijnRoutes.get(MijnRouteKey.SELF_SERVICE_DYNAMIC_PROCESS).path,
                canActivate: [DynamicProcessGuard],
                component: ApWebcomponentComponent,
                resolve: {
                    deployUrl: (route: ActivatedRouteSnapshot) => {
                        const webcomponent = dynamicTitles[route.params['webcomponent'] as keyof typeof dynamicTitles];
                        return `/online/webcomponents/${webcomponent}`;
                    },
                    identifier: (route: ActivatedRouteSnapshot) => {
                        const webcomponent = dynamicTitles[route.params['webcomponent'] as keyof typeof dynamicTitles];
                        return `${webcomponent}-webcomponent`;
                    }
                },
                data: {
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.ACTIE
                },
                children: [
                    {
                        path: 'mobile',
                        canActivate: [DynamicProcessGuard],
                        component: ApWebcomponentComponent,
                        resolve: {
                            deployUrl: (route: ActivatedRouteSnapshot) => {
                                const webcomponent = dynamicTitles[route.parent!.params['webcomponent'] as keyof typeof dynamicTitles];
                                return `/online/webcomponents/${webcomponent}`;
                            },
                            identifier: (route: ActivatedRouteSnapshot) => {
                                const webcomponent = dynamicTitles[route.parent!.params['webcomponent'] as keyof typeof dynamicTitles];
                                return `${webcomponent}-webcomponent`;
                            }
                        },
                        data: {
                            isMobile: true,
                            requiresAuth: false,
                            dynamicTemplate: 'NONE'
                        }
                    }
                ]
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.CONTACT).path,
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-contact',
                    identifier: 'mijn-contact-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.AFSPRAKENBEHEER).path,
                component: ApWebcomponentComponent,
                canActivate: [BusinessCustomerGuard],
                data: {
                    deployUrl: '/online/webcomponents/mijn-klant-afsprakenbeheer',
                    identifier: 'mijn-klant-afsprakenbeheer-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.CONTACT_TELEFONIE).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-contact-telefonie',
                    identifier: 'mijn-contact-telefonie-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.INSURANCES).path,
        title: 'Verzekeringen',
        children: [
            {
                path: '',
                component: ApWebcomponentComponent,

                data: {
                    deployUrl: '/online/webcomponents/mijn-verzekeren-beheer',
                    identifier: 'mijn-verzekeren-beheer-webcomponent',
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INSURANCES_DETAILS).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-verzekeren-beheer',
                    identifier: 'mijn-verzekeren-beheer-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.INSURANCES_DETAILS).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INSURANCES_ADDPRODUCT).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-verzekeren-beheer',
                    identifier: 'mijn-verzekeren-beheer-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.INSURANCES_ADDPRODUCT).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INSURANCES_PRODUCTPAGE).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-verzekeren-beheer',
                    identifier: 'mijn-verzekeren-beheer-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.INSURANCES_PRODUCTPAGE).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            },
            {
                path: MijnRoutes.get(MijnRouteKey.INSURANCES_MAINTENANCE).path,
                component: ApWebcomponentComponent,
                data: {
                    deployUrl: '/online/webcomponents/mijn-verzekeren-beheer',
                    identifier: 'mijn-verzekeren-beheer-webcomponent',
                    path: MijnRoutes.get(MijnRouteKey.INSURANCES_MAINTENANCE).internalPath,
                    events: defaultEvents,
                    dynamicTemplate: MijnOmgevingTemplates.OVERZICHT
                }
            }
        ]
    },
    {
        path: MijnRoutes.get(MijnRouteKey.WIJZIGEN_AFSCHRIFTFREQUENTIE).path,
        component: ApWebcomponentComponent,
        title: 'Wijzigen afschriftfrequentie',
        data: {
            deployUrl: '/online/webcomponents/mijn-wijzigen-afschriftfrequentie',
            identifier: 'mijn-wijzigen-afschriftfrequentie',
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ZAKELIJK_EXTRA_AFLOSSEN).path,
        component: ApWebcomponentComponent,
        data: {
            deployUrl: '/online/webcomponents/mijn-zakelijk-extra-aflossen',
            identifier: 'mijn-zakelijk-extra-aflossen-webcomponent',
            allowBusiness: true,
            events: defaultEvents,
            dynamicTemplate: MijnOmgevingTemplates.ACTIE
        }
    },
    {
        path: 'telefoonnummer-validatie',
        title: 'Telefoonnummer valideren',
        component: PhoneNumberValidationComponent,
        data: {
            allowBusiness: true,
            events: defaultEvents
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.ERROR).path,
        title: 'Fout',
        component: MijnContextErrorComponent,
        data: {
            context: ErrorContext.GENERIEK
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.SORRY).path,
        title: 'Sorry',
        component: MijnContextErrorComponent,
        data: {
            context: ErrorContext.BETA_UITLEG
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.PAGE_NOT_FOUND).path,
        title: 'Niet gevonden',
        component: MijnContextErrorComponent,
        data: {
            context: ErrorContext.NIET_GEVONDEN
        }
    },
    {
        path: MijnRoutes.get(MijnRouteKey.BUSINESS_CUSTOMER_BLOCKED).path,
        component: MijnContextErrorComponent,
        title: 'Fout',
        data: {
            context: ErrorContext.ZAKELIJK_GEBLOKKEERD
        }
    },
    {
        path: '**',
        component: MijnContextErrorComponent,
        canActivate: [CorrectRouteGuard],
        title: 'Fout',
        data: {
            context: ErrorContext.NIET_GEVONDEN
        }
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { useHash: false, enableTracing: true }), CommonModule],
    exports: [RouterModule]
})
export class MijnOmgevingRouterModule {}
